import React, { useMemo } from 'react';

interface Props {
    morse: string;
    playingIndex: number;
}

interface ParsedLetter {
    symbols: Array<'.' | '-'>;
}

interface ParsedWord {
    letters: ParsedLetter[];
}

function parseMorse(morse: string): ParsedWord[] {
    return morse.split(/\s*\/\s*/).map((word) => ({
        letters: word
            .trim()
            .split(/\s+/)
            .filter(Boolean)
            .map((letter) => ({
                symbols: letter.split('').filter((c): c is '.' | '-' => c === '.' || c === '-'),
            })),
    }));
}

export function MorseVisualizer({ morse, playingIndex }: Props) {
    const words = useMemo(() => parseMorse(morse), [morse]);
    let cursor = 0;

    const total = words.reduce(
        (acc, w) => acc + w.letters.reduce((a, l) => a + l.symbols.length, 0),
        0,
    );

    if (total === 0) {
        return (
            <div className="mv-frame mv-empty">
                <span className="mv-empty-text">— silence —</span>
            </div>
        );
    }

    return (
        <div className="mv-frame">
            <div className="mv-rail">
                {words.map((word, wIdx) => (
                    <React.Fragment key={wIdx}>
                        <div className="mv-word">
                            {word.letters.map((letter, lIdx) => (
                                <div className="mv-letter" key={lIdx}>
                                    {letter.symbols.map((sym, sIdx) => {
                                        const idx = cursor++;
                                        const isActive = idx === playingIndex;
                                        const isPast = playingIndex >= 0 && idx < playingIndex;
                                        return (
                                            <span
                                                key={sIdx}
                                                className={[
                                                    'mv-sym',
                                                    sym === '.' ? 'mv-dot' : 'mv-dash',
                                                    isActive ? 'is-active' : '',
                                                    isPast ? 'is-past' : '',
                                                ]
                                                    .filter(Boolean)
                                                    .join(' ')}
                                            />
                                        );
                                    })}
                                </div>
                            ))}
                        </div>
                        {wIdx < words.length - 1 && <span className="mv-slash" aria-hidden="true">/</span>}
                    </React.Fragment>
                ))}
            </div>
        </div>
    );
}
